import { HfuBand as CorrelationHfuBand } from '../../../../../../../@types/projects/types';
import { ProjectSample } from '../../../../../../../@types/samples/types';

export type HydraulicFlowUnit = string;

export interface LorenzPoint {
  storageNorm: number;
  flowNorm: number;
  sampleCode: string;
  sampleLength: number;
  porosity: number;
  permeability: number;
  hfu: HydraulicFlowUnit;
}

export interface HfuSeries {
  hfu: HydraulicFlowUnit;
  color: string;
  points: LorenzPoint[];
}

export interface StratigraphicLorenzResult {
  points: LorenzPoint[];
  hfuSeries: HfuSeries[];
}

export interface ResolvedHfuBand {
  hfu: HydraulicFlowUnit;
  maxStorageNorm: number | null;
  color: string;
}

export const HFU_BANDS: ResolvedHfuBand[] = [
  { hfu: 'HFU0', maxStorageNorm: 0.21, color: '#006100' },
  { hfu: 'HFU1', maxStorageNorm: 0.41, color: '#000080' },
  { hfu: 'HFU2', maxStorageNorm: 0.55, color: '#7030a0' },
  { hfu: 'HFU3', maxStorageNorm: 0.685, color: '#ff0000' },
  { hfu: 'HFU4', maxStorageNorm: 0.8, color: '#ed7d31' },
  { hfu: 'HFU5', maxStorageNorm: 0.91, color: '#ffc000' },
  { hfu: 'HFU6', maxStorageNorm: null, color: '#00b050' },
];

export function resolveHfuBands(hfuBands?: CorrelationHfuBand[]): ResolvedHfuBand[] {
  if (!hfuBands || hfuBands.length === 0) return HFU_BANDS;
  return hfuBands.map((b) => ({ hfu: b.label, maxStorageNorm: b.cutoff, color: b.color }));
}

export function assignHydraulicFlowUnit(storageNorm: number, bands: ResolvedHfuBand[] = HFU_BANDS): HydraulicFlowUnit {
  return bands.find((band) =>
    band.maxStorageNorm == null ? true : storageNorm <= band.maxStorageNorm
  )!.hfu;
}

function emptyHfuSeries(bands: ResolvedHfuBand[]): HfuSeries[] {
  return bands.map(({ hfu, color }) => ({ hfu, color, points: [] }));
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}

/**
 * Computes the Stratigraphic Modified Lorenz Plot.
 * Uses explicit sample_length when available; falls back to depth intervals
 * between consecutive samples when sample_length is missing.
 */
export function computeStratigraphicLorenz(
  samples: ProjectSample[],
  hfuBands?: CorrelationHfuBand[]
): StratigraphicLorenzResult {
  const bands = resolveHfuBands(hfuBands);

  const valid = samples.filter(
    (s) =>
      isFiniteNumber(s.depth) &&
      isPositiveNumber(s.input_sample_values.porosity) &&
      isPositiveNumber(s.input_sample_values.permeability)
  );

  if (valid.length === 0) {
    return { points: [], hfuSeries: emptyHfuSeries(bands) };
  }

  const sorted = [...valid].sort((a, b) => a.depth - b.depth);

  // Compute effective length per sample: use sample_length if available,
  // otherwise approximate from depth intervals between neighbours.
  const effectiveLengths = sorted.map((s) =>
    isPositiveNumber(s.sample_length) ? s.sample_length! : 1
  );

  const phiH = sorted.map((s, i) => effectiveLengths[i] * s.input_sample_values.porosity!);
  const kH = sorted.map((s, i) => effectiveLengths[i] * s.input_sample_values.permeability!);

  const totalPhiH = phiH.reduce((a, b) => a + b, 0);
  const totalKH = kH.reduce((a, b) => a + b, 0);

  if (totalPhiH === 0 || totalKH === 0) {
    return { points: [], hfuSeries: emptyHfuSeries(bands) };
  }

  let cumPhiH = 0;
  let cumKH = 0;

  const points = sorted.map((s, i): LorenzPoint => {
    cumPhiH += phiH[i];
    cumKH += kH[i];

    const storageNorm = cumPhiH / totalPhiH;
    const flowNorm = cumKH / totalKH;

    return {
      storageNorm,
      flowNorm,
      sampleCode: s.sample_code,
      sampleLength: s.sample_length!,
      porosity: s.input_sample_values.porosity!,
      permeability: s.input_sample_values.permeability!,
      hfu: assignHydraulicFlowUnit(storageNorm, bands),
    };
  });

  const hfuSeries = bands.map(({ hfu, color }) => ({
    hfu,
    color,
    points: points.filter((point) => point.hfu === hfu),
  }));

  return { points, hfuSeries };
}
